import { Component, OnInit, OnDestroy } from "@angular/core";
import { PageService } from "@services/page/page.service";
import { IPage, IFormOptions } from "@interfaces/index";
import { Subject } from "rxjs";
import {
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  filter,
  tap,
  switchMap,
} from "rxjs/operators";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { HelperService } from "@services/helper/helper.service";
import { IModalvalue } from "@shared-models/index";
import {
  TrackingService,
  UserService,
  AffiliateManagersService,
} from "@services/index";

@Component({
  selector: "aff-event-types",
  templateUrl: "./event-types.component.html",
  styleUrls: ["./event-types.component.scss"],
})
export class EventTypesPage implements OnInit, OnDestroy {
  form: FormGroup;

  formType: "edit" | "create" = "create";

  confirmationModal: boolean = false;
  deleteValues: string = "";

  isFormOpen: boolean = false;
  loadingIndicator: boolean = true;

  rows: any[] = [];
  page: IPage = {
    total: 0,
    maxPages: 0,
    perPage: 0,
    currentPage: 0,
  };

  managers: any[];
  parents: any[];

  private unsubscribe$ = new Subject<void>();

  constructor(
    private trackingService: TrackingService,
    private pageService: PageService,
    private fb: FormBuilder,
    private userService: UserService,
    private affiliateManagersService: AffiliateManagersService
  ) {
    this.setForms();
    this.setBreadcrum();
    this.getManager();

    this.form
      .get("user_id")
      .valueChanges.pipe(
        takeUntil(this.unsubscribe$),
        debounceTime(500),
        distinctUntilChanged(),
        tap((user_id) => {
          this.trackingService.fetchParentEventTypes(1, { user_id });
        }),
        tap((user_id) =>
          user_id
            ? this.form.get("parent_id").enable()
            : this.form.get("parent_id").disable()
        ),
        switchMap(() => this.trackingService.getParentEventTypesSelector()),
        tap((parents) => (this.parents = parents))
      )
      .subscribe();

    this.trackingService.fetchEventTypes(1, {});
  }

  ngOnInit(): void {
    this.trackingService
      .getEventTypesSelector()
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(({ isLoaded }) => isLoaded),
        tap(({ data }) => (this.rows = data)),
        tap((state) => this.setPagination(state)),
        tap(() => (this.loadingIndicator = false))
      )
      .subscribe();

    this.trackingService
      .getEventTypeSelector()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        if (res.id) {
          this.form.patchValue({
            id: res.id,
            name: res.name,
            user_id: res.user_id,
            parent_id: res.parent ? res.parent.id : null,
          });
        }
      });
  }

  setPage(page) {
    this.trackingService.fetchEventTypes(page.offset + 1, {});
  }

  onCreate() {
    this.formType = "create";

    this.openFormModal();
    this.form.reset();
  }

  onEdit(row: any): void {
    this.formType = "edit";
    this.trackingService.fetchEventType(row.event_type_id);

    this.openFormModal();
  }

  onDelete(row: any): void {
    this.deleteValues = `Event Type: <br><br> ${row.event_type_name}`;

    this.form.get("id").patchValue(row.event_type_id);
    this.openConfirmeModal();
  }

  onDeleteConfirm(options: IModalvalue): void {
    if (options.confirm) {
      this.trackingService.deleteEventType(this.form.value.id);
    }

    this.form.get("id").reset();
    this.closeConfirmeModal();
  }

  onChangeFormValue(options: IFormOptions): void {
    this.form.patchValue({ [options.name]: options.value });
  }

  submit() {
    if (!this.form.valid) return;

    const method =
      this.formType === "create"
        ? this.trackingService.addEventType.bind(this.trackingService)
        : this.trackingService.updateEventType.bind(this.trackingService);

    method(this.form.value);

    this.closeFormModal();
    this.form.reset();
  }

  private setPagination(state) {
    this.page = {
      currentPage: state.currentPage - 1,
      perPage: state.perPage,
      maxPages: state.maxPages,
      total: state.total,
    };
  }

  private getManager() {
    this.userService
      .getProfileSelector()
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((user) => !HelperService.isObjectEmpty(user)),
        tap(({ isManager }) => !isManager && this.getAllManagers()),
        filter(({ isManager }) => isManager),
        tap(({ userId }) => this.form.patchValue({ user_id: +userId }))
      )
      .subscribe();
  }

  private getAllManagers() {
    this.affiliateManagersService.fetchAffiliateManagers(1, {});

    this.affiliateManagersService
      .getAffiliateManagersSelector()
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(({ isLoaded }) => isLoaded),
        tap(({ data }) => (this.managers = data))
      )
      .subscribe();
  }

  private openFormModal(): void {
    this.isFormOpen = true;
  }

  closeFormModal(): void {
    this.isFormOpen = false;
  }

  private openConfirmeModal() {
    this.confirmationModal = true;
  }

  private closeConfirmeModal() {
    this.confirmationModal = false;
  }

  private setForms() {
    this.form = this.fb.group({
      id: [null],
      name: [null, [Validators.required]],
      user_id: [null, [Validators.required]],
      parent_id: [{ value: null, disabled: true }],
    });
  }

  private setBreadcrum() {
    this.pageService.changePageInfo({ breadcrum: ["Tracking", "Event Types"] });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
